import {
  type FeedbackItem,
  type FeedbackItemBase,
  type LinkedElement,
  type PinPosition,
} from "@previewloop/shared";
import { createEffect, createRoot, createSignal } from "solid-js";
import { cssSelector } from "../utils/selector";
import { createTargetHighlight } from "../utils/target-highlight";
import { submitBatchFeedback, SubmitError } from "../utils/submit";
import {
  createIdbPersistence,
  createUnsentMirror,
  type PersistedEntry,
  type PersistedDraftTarget,
  type PersistedRecord,
} from "../utils/unsent-persistence";
import { type DrawController, type DrawTool } from "../utils/widget-draw";
import { type WidgetConfig, type WidgetDeps } from "./widget-types";

const TARGET_CLASS = "pl-previewloop-target";
/** On the page root while comment mode is armed; the injected page CSS maps it to a crosshair. */
const COMMENTING_CLASS = "pl-previewloop-commenting";

/** A Capture collected into the unsent batch. Shape-identical to what the
    unsent mirror stores - one source of truth, no mapping layer. */
export type CollectedEntry = PersistedEntry;

export type SelectedTarget = LinkedElement & { element: Element; pin: PinPosition };
export type SelectedTargets = [SelectedTarget, ...SelectedTarget[]];

export type ComposerSession = {
  label: number;
  initialTargets: SelectedTargets;
};

export type ActivePin = {
  id: string;
  /** Sticky bubbles (opened by click) survive hover-out and close explicitly. */
  sticky: boolean;
};

export type ToastState = {
  id: string;
  message: string;
  variant: "ok" | "err" | "pending";
  detail?: string;
};

/** Newest-visible cap; older toasts are dropped when the stack overflows. */
const MAX_TOASTS = 3;

const retryHint = (seconds?: number): string => {
  if (!seconds || seconds <= 0) return "try again shortly";
  if (seconds < 45) return `try again in ${seconds}s`;
  if (seconds <= 90) return "try again in about a minute";
  return `try again in about ${Math.round(seconds / 60)} minutes`;
};

const rejectionMessage = (err: unknown): string => {
  if (err instanceof SubmitError) {
    if (err.code === "rate_limited" || err.status === 429) {
      return `Too many submissions - ${retryHint(err.retryAfter)}`;
    }
    if (err.code === "origin_not_allowed") {
      return `${err.origin ?? "This page's origin"} isn't in this project's allowlist`;
    }
    if (err.code === "unknown_project_key") {
      return "Project key not recognized - check the widget embed or extension options";
    }
    if (err.status === 403) {
      return "This page isn't registered for this project - check the origin allowlist";
    }
  }
  return "Couldn't send - try again";
};

const targetText = (el: Element, maxLen: number): string =>
  (el.textContent ?? "").trim().slice(0, maxLen);

const selectedTargetFor = (element: Element, pin: PinPosition): SelectedTarget => {
  const text = targetText(element, 200);
  return { element, pin, selector: cssSelector(element), elementText: text || undefined };
};

const buildItem = (
  comment: string,
  targets: SelectedTargets,
  pin: PinPosition,
  meta: Partial<FeedbackItemBase> = {},
): FeedbackItem => {
  return {
    comment,
    pageUrl: location.href,
    linkedElements: targets.map(({ selector, elementText }) => ({ selector, elementText })) as [
      LinkedElement,
      ...LinkedElement[],
    ],
    pin,
    browser: {
      userAgent: navigator.userAgent,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
    },
    ...meta,
  };
};

let nextId = 0;
const uid = (): string => String(++nextId);

export type WidgetStore = ReturnType<typeof createWidgetStore>;

export type CreateWidgetStoreOptions = {
  config: WidgetConfig;
  deps: WidgetDeps;
  host: HTMLElement;
  draw: DrawController;
  capture: () => Promise<Blob>;
};

export function createWidgetStore(options: CreateWidgetStoreOptions) {
  return createRoot((dispose) => {
    const { config, deps, host, draw, capture } = options;

    const [commentMode, setCommentMode] = createSignal(false);
    const [composer, setComposer] = createSignal<ComposerSession | null>(null);
    const [composerTargets, setComposerTargets] = createSignal<SelectedTargets | null>(null);
    const [collected, setCollected] = createSignal<CollectedEntry[]>([]);
    const [activePin, setActivePin] = createSignal<ActivePin | null>(null);
    const [reviewOpen, setReviewOpen] = createSignal(false);
    const [drawMode, setDrawModeSignal] = createSignal(false);
    const [drawTool, setDrawToolSignal] = createSignal<DrawTool>("pen");
    const [toasts, setToasts] = createSignal<ToastState[]>([]);
    /** Ids mid-exit. Kept beside the list so dismissal never swaps the toast
        objects - <For> keys by reference, and a swapped row would remount and
        replay its entrance as a flash right before removal. */
    const [leavingToastIds, setLeavingToastIds] = createSignal<ReadonlySet<string>>(new Set());
    const [sendAllBusy, setSendAllBusy] = createSignal(false);
    const [linkingEntryId, setLinkingEntryId] = createSignal<string | null>(null);

    /** True once the open composer has text; a dirty draft is never discarded
        by a stray page click - it must be submitted or explicitly cancelled. */
    let composerDirty = false;
    /** Live composer text, mirrored so unsent work survives navigation. */
    let draftText = "";
    /** Prefill for a composer restored from persistence (keyed remounts read it once). */
    const [composerDraft, setComposerDraftSignal] = createSignal("");

    const pageKey = (): string => location.origin + location.pathname + location.search;

    // The unsent batch is mirrored to persistent storage per page URL (hash
    // ignored - pins are page-bound coordinates) and restored on mount, so
    // navigating away can never lose a review. The mirror owns all timing;
    // this store only says what the unsent state is and how to re-apply it.
    const mirror = createUnsentMirror({
      persistence: deps.persistence ?? createIdbPersistence(),
      pageKey,
      snapshot: () => {
        const entries = collected();
        const session = composer();
        const targets = composerTargets();
        const draft =
          session && targets && draftText.trim()
            ? {
                text: draftText,
                targets: targets.map(({ selector, elementText, pin }) => ({
                  selector,
                  elementText,
                  pin,
                })) as [PersistedDraftTarget, ...PersistedDraftTarget[]],
              }
            : undefined;
        if (entries.length === 0 && !draft) return null;
        return { savedAt: Date.now(), entries, draft };
      },
      onRestore: (record) => applyRestoredRecord(record),
    });
    const schedulePersist = mirror.schedule;
    const flushPersist = mirror.flush;

    // Reason-based highlight ownership: hover, the selected target, and an
    // open card each hold their own reason; the class stays while any remain.
    const highlight = createTargetHighlight(TARGET_CLASS);

    /** Mark the toast as leaving; the view plays the exit animation and calls
        removeToast when it resolves, so the timing has one owner. */
    const dismissToast = (id: string): void => {
      // Auto-timer and click can race; the exit must only start once. An
      // evicted toast's auto-timer must not park its id in the set forever.
      if (leavingToastIds().has(id)) return;
      if (!toasts().some((toast) => toast.id === id)) return;
      setLeavingToastIds((prev) => new Set(prev).add(id));
    };

    const removeToast = (id: string): void => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
      setLeavingToastIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    };

    const showToast = (
      message: string,
      variant: "ok" | "err" | "pending" = "ok",
      detail?: string,
    ): string => {
      const id = uid();
      setToasts((prev) => {
        const next = [...prev, { id, message, variant, detail }];
        // Overflow evicts the oldest non-pending toast - a pending one is a
        // live progress indicator and must survive the stack cap.
        while (next.length > MAX_TOASTS) {
          const index = next.findIndex((toast) => toast.variant !== "pending" && toast.id !== id);
          if (index === -1) break;
          const [dropped] = next.splice(index, 1);
          setLeavingToastIds((ids) => {
            if (!dropped || !ids.has(dropped.id)) return ids;
            const cleaned = new Set(ids);
            cleaned.delete(dropped.id);
            return cleaned;
          });
        }
        return next;
      });
      if (variant !== "pending") {
        setTimeout(() => dismissToast(id), 3200);
      }
      return id;
    };

    const setDrawTool = (tool: DrawTool): void => {
      setDrawToolSignal(tool);
      draw.setTool(tool);
    };

    const setDrawMode = (on: boolean): void => {
      if (on === drawMode()) return;
      setDrawModeSignal(on);
      if (on) setDrawTool("pen");
      draw.setEnabled(on);
    };

    const closeComposerDraft = (): void => {
      composerDirty = false;
      draftText = "";
      setComposerDraftSignal("");
      schedulePersist();
      // Unclaimed session strokes die with the card; submitted ones were
      // already moved into their entry.
      draw.discardSession();
      setComposer(null);
      setComposerTargets(null);
      highlight.moveReason("selected", null);
    };

    const exitCommentMode = (): void => {
      closeComposerDraft();
      setLinkingEntryId(null);
      if (!commentMode()) return;
      setCommentMode(false);
      document.documentElement.classList.remove(COMMENTING_CLASS);
      document.removeEventListener("click", onPageClick, true);
      document.removeEventListener("mousemove", onPageMove, true);
      highlight.moveReason("hover", null);
    };

    const enterCommentMode = (): void => {
      setDrawMode(false);
      if (commentMode()) return;
      setActivePin(null);
      setCommentMode(true);
      document.documentElement.classList.add(COMMENTING_CLASS);
      document.addEventListener("click", onPageClick, true);
      document.addEventListener("mousemove", onPageMove, true);
    };

    const closeActivePin = (): void => {
      setActivePin(null);
    };

    // Keep the ring + tint on the annotated element while its card is open.
    createEffect(() => {
      const active = activePin();
      const entry = active ? collected().find((candidate) => candidate.id === active.id) : null;
      let next: Element[] = [];
      if (entry) {
        for (const linked of entry.item.linkedElements) {
          try {
            const element = document.querySelector(linked.selector);
            if (element) next.push(element);
          } catch {
            // Ignore stale selectors.
          }
        }
      }
      highlight.setReason("entry", next);
    });

    /** Global: composer first, then the armed mode, then any open pin bubble. */
    const onKeydown = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      if (composer()) {
        closeComposerDraft();
        return;
      }
      if (commentMode()) {
        exitCommentMode();
        return;
      }
      if (activePin()) closeActivePin();
    };
    document.addEventListener("keydown", onKeydown, true);

    const isWidgetEvent = (e: MouseEvent): boolean => e.composedPath().includes(host);

    const onPageMove = (e: MouseEvent): void => {
      const target = e.target;
      if (
        !(target instanceof Element) ||
        isWidgetEvent(e) ||
        target === document.body ||
        target === document.documentElement ||
        composerTargets()?.some((selected) => selected.element === target)
      ) {
        // Off the page or back on the selected element (which glows through
        // its own reason): the hover highlight retires.
        highlight.moveReason("hover", null);
        return;
      }
      highlight.moveReason("hover", target);
    };

    /** Page clicks append to the open annotation's ordered target collection. */
    const onPageClick = (e: MouseEvent): void => {
      const target = e.target;
      if (!(target instanceof Element) || isWidgetEvent(e)) return;
      e.preventDefault();
      e.stopPropagation();
      highlight.moveReason("hover", null);
      const pin = {
        x: e.clientX,
        y: e.clientY,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      };
      const linkingId = linkingEntryId();
      if (linkingId) {
        const selected = selectedTargetFor(target, pin);
        const linkingEntry = collected().find((entry) => entry.id === linkingId);
        if (
          !linkingEntry ||
          linkingEntry.item.linkedElements.some((linked) => linked.selector === selected.selector)
        ) {
          return;
        }
        setCollected((entries) =>
          entries.map((entry) => {
            if (entry.id !== linkingId) return entry;
            return {
              ...entry,
              item: {
                ...entry.item,
                linkedElements: [
                  ...entry.item.linkedElements,
                  { selector: selected.selector, elementText: selected.elementText },
                ],
              },
              targetPins: [...(entry.targetPins ?? [entry.pin]), pin],
            };
          }),
        );
        setLinkingEntryId(null);
        return;
      }
      const session = composer();
      if (session) {
        const current = composerTargets();
        if (!current || current.some((selected) => selected.element === target)) return;
        const next = [...current, selectedTargetFor(target, pin)] as SelectedTargets;
        setComposerTargets(next);
        highlight.setReason(
          "selected",
          next.map((selected) => selected.element),
        );
        schedulePersist();
        return;
      }
      openComposer(target, pin);
    };

    const captureAndCollect = async (
      comment: string,
      targets: SelectedTargets,
      pin: PinPosition,
    ): Promise<string | null> => {
      // The capture provider is responsible for excluding the widget from the
      // screenshot (html2canvas ignores the host; the extension hides it while
      // grabbing real pixels). Hiding the host here made the whole UI blink on
      // every submit.
      try {
        const screenshot = await capture();
        const id = uid();
        setCollected((prev) => [
          ...prev,
          {
            id,
            item: buildItem(comment, targets, pin, deps.itemMeta?.()),
            screenshot,
            pin,
            targetPins: targets.map((target) => target.pin),
          },
        ]);
        return id;
      } catch {
        showToast("Couldn't capture - try again", "err");
        return null;
      }
    };

    const sendAllBatch = async (): Promise<void> => {
      const items = collected();
      if (items.length === 0) return;
      setSendAllBusy(true);
      const pendingId = showToast("Sending…", "pending", "Uploading feedback");
      try {
        await submitBatchFeedback(
          config.apiOrigin,
          { projectKey: config.projectKey, items: items.map((c) => c.item) },
          items.map((c) => c.screenshot),
          { fetch: deps.fetch },
        );
        setCollected([]);
        setActivePin(null);
        setReviewOpen(false);
        // Sent feedback must leave storage now - a debounced clear can lose
        // the race against the tab closing and restore as a duplicate later.
        flushPersist();
        dismissToast(pendingId);
        // A dirty draft is never discarded silently - it stays open as the
        // start of the next batch; only clean sessions tear down fully.
        if (!(composer() && composerDirty)) exitCommentMode();
        draw.clear();
        showToast("Feedback sent", "ok", "Thanks for the review");
      } catch (err) {
        dismissToast(pendingId);
        showToast(rejectionMessage(err), "err");
      } finally {
        setSendAllBusy(false);
      }
    };

    const openComposer = (target: Element, pin: PinPosition): void => {
      setActivePin(null);
      draftText = "";
      setComposerDraftSignal("");
      const label = collected().length + 1;
      const targets: SelectedTargets = [selectedTargetFor(target, pin)];
      setComposerTargets(targets);
      setComposer({ label, initialTargets: targets });
      highlight.setReason("selected", [target]);
    };

    /** A released draw stroke opens the composer at its end point; the pen
        stays armed for more strokes. A dirty draft is never displaced. */
    const annotateFromDraw = (point: { x: number; y: number }): void => {
      if (composer() && composerDirty) return;
      // Validate the target before touching any state: an invalid release
      // leaves the stroke in the session for the next valid one to claim.
      const canvas = document.querySelector<HTMLElement>("[data-pl-draw]");
      const pointerEventsBefore = canvas?.style.pointerEvents ?? "";
      if (canvas) canvas.style.pointerEvents = "none";
      const target = document.elementFromPoint(point.x, point.y);
      if (canvas) canvas.style.pointerEvents = pointerEventsBefore;
      if (!(target instanceof Element) || target === document.documentElement) return;
      if (host.contains(target)) return;
      // Keep the session across the reopen: the card owns all strokes drawn
      // since the last close, not only the latest one.
      composerDirty = false;
      setComposer(null);
      openComposer(target, {
        x: point.x,
        y: point.y,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      });
    };

    /** Capture + collect only; the composer owns its busy/success morph and
        calls closeComposerDraft afterwards. Returns whether it collected. */
    const onAddCapture = async (comment: string): Promise<boolean> => {
      const session = composer();
      const targets = composerTargets();
      if (!session || !targets) return false;
      // Snapshot before the capture: strokes drawn while the screenshot is in
      // flight are not in it, so they must not attach to this entry either.
      const strokes = draw.snapshotSession();
      const id = await captureAndCollect(comment, targets, targets[0].pin);
      if (!id) return false;
      if (strokes.length > 0) {
        draw.removeStrokes(strokes);
        setCollected((prev) =>
          prev.map((entry) => (entry.id === id ? { ...entry, strokes } : entry)),
        );
      }
      return true;
    };

    /** Ghost an entry's saved page-space strokes onto the page. */
    const previewEntryStrokes = (id: string | null): void => {
      const entry = id ? collected().find((candidate) => candidate.id === id) : null;
      draw.setOverlay(entry?.strokes?.length ? entry.strokes : null);
    };

    const updateCollected = (id: string, comment: string): void => {
      setCollected((prev) =>
        prev.map((entry) =>
          entry.id === id ? { ...entry, item: { ...entry.item, comment } } : entry,
        ),
      );
    };

    const removeCollected = (id: string): void => {
      setActivePin((cur) => (cur?.id === id ? null : cur));
      setLinkingEntryId((linkingId) => (linkingId === id ? null : linkingId));
      setCollected((prev) => {
        const next = prev.filter((entry) => entry.id !== id);
        if (next.length === 0) setReviewOpen(false);
        return next;
      });
    };

    const removeCollectedTarget = (id: string, index: number): void => {
      setCollected((entries) =>
        entries.map((entry) => {
          if (entry.id !== id || entry.item.linkedElements.length === 1) return entry;
          const linkedElements = entry.item.linkedElements.filter(
            (_, targetIndex) => targetIndex !== index,
          );
          const targetPins = (
            entry.targetPins ?? entry.item.linkedElements.map(() => entry.pin)
          ).filter((_, targetIndex) => targetIndex !== index);
          const [primary, ...additional] = linkedElements;
          const [pin, ...additionalPins] = targetPins;
          if (!primary || !pin) return entry;
          return {
            ...entry,
            item: { ...entry.item, linkedElements: [primary, ...additional] },
            pin,
            targetPins: [pin, ...additionalPins],
          };
        }),
      );
    };

    const beginLinkingEntry = (id: string): void => {
      if (!collected().some((entry) => entry.id === id)) return;
      setLinkingEntryId(id);
      if (commentMode()) return;
      setCommentMode(true);
      document.documentElement.classList.add(COMMENTING_CLASS);
      document.addEventListener("click", onPageClick, true);
      document.addEventListener("mousemove", onPageMove, true);
    };

    const openPinHover = (id: string): void => {
      if (composer()) return;
      setActivePin((cur) => (cur?.sticky ? cur : { id, sticky: false }));
    };

    const closePinHover = (id: string): void => {
      setActivePin((cur) => (cur && cur.id === id && !cur.sticky ? null : cur));
    };

    const togglePinSticky = (id: string): void => {
      // One bubble at a time: pins are inert while a composer is open.
      if (composer()) return;
      setActivePin((cur) => (cur?.id === id && cur.sticky ? null : { id, sticky: true }));
    };

    /** Editing a bubble pins it open so hover-out can't discard the draft. */
    const makePinSticky = (id: string): void => {
      setActivePin({ id, sticky: true });
    };

    const setComposerDirty = (dirty: boolean): void => {
      composerDirty = dirty;
    };

    /** Mirror of the composer textarea, persisted so a draft survives navigation. */
    const setComposerDraft = (text: string): void => {
      draftText = text;
      schedulePersist();
    };

    const removeComposerTarget = (index: number): void => {
      const session = composer();
      const current = composerTargets();
      if (!session || !current || current.length === 1 || !current[index]) return;
      const targets = current.filter((_, targetIndex) => targetIndex !== index) as SelectedTargets;
      setComposerTargets(targets);
      highlight.setReason(
        "selected",
        targets.map((target) => target.element),
      );
      schedulePersist();
    };

    const toggleReview = (): void => {
      setReviewOpen((open) => !open);
    };

    const closeReview = (): void => {
      setReviewOpen(false);
    };

    const clearCollected = (): void => {
      setActivePin(null);
      setCollected([]);
      setReviewOpen(false);
      flushPersist();
    };

    // SPA navigation (#113): pins, highlights, and the unsent batch are
    // page-bound; on a client-side route change they would stay painted over
    // an unrelated page. A URL change is treated exactly like a full reload:
    // the departed page's batch persists under its own key (the mirror
    // snapshots before the reset), page-bound state resets, and the new
    // page's record restores. Comment mode stays armed - a reviewer often
    // navigates on purpose to keep annotating.
    const onPageChange = (): void => {
      if (!mirror.pageChange()) return;
      closeComposerDraft();
      previewEntryStrokes(null);
      highlight.moveReason("hover", null);
      clearCollected();
      // Unclaimed strokes are page coordinates over a page that is gone.
      draw.clear();
    };

    /** Scroll the page to an entry's pin and open its annotation card. */
    const focusEntry = (id: string): void => {
      const entry = collected().find((candidate) => candidate.id === id);
      if (!entry) return;
      if (composer()) {
        if (composerDirty) return;
        closeComposerDraft();
      }
      window.scrollTo({
        top: Math.max(0, entry.pin.scrollY + entry.pin.y - window.innerHeight / 2),
        left: Math.max(0, entry.pin.scrollX + entry.pin.x - window.innerWidth / 2),
        behavior: "smooth",
      });
      setActivePin({ id, sticky: true });
    };

    const toggleDrawMode = (): void => {
      const next = !drawMode();
      if (next) exitCommentMode();
      setDrawMode(next);
    };

    // Mirror every entry change; the mirror itself gates writes until its
    // initial restore settled.
    createEffect(() => {
      collected();
      schedulePersist();
    });

    // The load is async and the user may have acted meanwhile - merge, and
    // never clobber live state with stored state.
    function applyRestoredRecord(record: PersistedRecord): void {
      if (record.entries.length > 0) {
        // Fresh ids: the persisted ones came from a previous session's
        // counter and would collide with this one's.
        const restored = record.entries.map((entry) => ({ ...entry, id: uid() }));
        setCollected((prev) => [...restored, ...prev]);
        const n = record.entries.length;
        showToast(
          `Restored ${n} unsent ${n === 1 ? "comment" : "comments"}`,
          "ok",
          "From your last visit",
        );
      }
      if (record.draft && !composer()) {
        const restoredTargets = record.draft.targets.flatMap((target) => {
          try {
            const element = document.querySelector(target.selector);
            return element ? [{ ...target, element }] : [];
          } catch {
            return [];
          }
        });
        // The annotated element is gone (page changed): the draft has no
        // anchor to reopen on - let it lapse rather than pin it to nothing.
        if (!restoredTargets[0]) return;
        const targets = restoredTargets as SelectedTargets;
        highlight.setReason(
          "selected",
          targets.map((target) => target.element),
        );
        draftText = record.draft.text;
        composerDirty = true;
        setComposerDraftSignal(record.draft.text);
        setComposerTargets(targets);
        setComposer({ label: collected().length + 1, initialTargets: targets });
      }
    }

    const disposeStore = (): void => {
      highlight.clear();
      exitCommentMode();
      document.removeEventListener("keydown", onKeydown, true);
      mirror.dispose();
      draw.setEnabled(false);
      dispose();
    };

    return {
      commentMode,
      composer,
      composerTargets,
      collected,
      activePin,
      reviewOpen,
      drawMode,
      toasts,
      leavingToastIds,
      dismissToast,
      removeToast,
      sendAllBusy,
      enterCommentMode,
      exitCommentMode,
      onAddCapture,
      annotateFromDraw,
      previewEntryStrokes,
      closeComposerDraft,
      updateCollected,
      removeCollected,
      removeCollectedTarget,
      beginLinkingEntry,
      linkingEntryId,
      openPinHover,
      closePinHover,
      togglePinSticky,
      makePinSticky,
      setComposerDirty,
      setComposerDraft,
      removeComposerTarget,
      composerDraft,
      closeActivePin,
      toggleReview,
      closeReview,
      clearCollected,
      focusEntry,
      toggleDrawMode,
      setDrawMode,
      drawTool,
      setDrawTool,
      sendAllBatch,
      clearDrawing: () => draw.clear(),
      onPageChange,
      dispose: disposeStore,
    };
  });
}
